import type { Ref } from 'vue';

import { ModalName } from '@/constants/modals';
import { toManageOrderMissionForm } from '@/modules/companyNeed/helpers/manageOrderModal';
import { mapCompanyNeedAddressToOrderAddress } from '@/modules/companyNeed/helpers/mission';
import missionService from '@/modules/companyNeed/services/missionService';
import { useCompanyNeedStore } from '@/modules/companyNeed/store/companyNeedStore';
import { useMissionPatchStore } from '@/modules/companyNeed/store/missionPatchStore';
import {
	InitialTabType,
	type MissionBulkWorker,
} from '@/modules/companyNeed/types/missions';
import {
	MissionAction,
	type MissionList,
	MissionStatus,
} from '@/modules/companyNeed/views/details/missions/types';
import { WorkerMissionStatus } from '@/types/mission';

import type { MissionRowPatch } from '@/modules/companyNeed/store/missionPatchStore';

import { useModalStore } from '@/dsComponents/modals/modalStore';

const CANCELED_REMOVED_ACTIONS: MissionAction[] = [
	MissionAction.VALIDATE,
	MissionAction.CANCEL,
	MissionAction.EDIT,
	MissionAction.DUPLICATE,
	MissionAction.EXTEND,
	MissionAction.ADD_WORKERS,
	MissionAction.SEND_TO_EVOLIA,
];

// biome-ignore lint/correctness/noUnusedVariables: Init Biome
interface UseMissionActionHandlersOptions {
	currentTab?: Ref<InitialTabType>;
}

/**
 * Composable that provides mission action handlers.
 * Can be used in different contexts (table view, modal view, etc.).
 *
 * @returns Object containing all mission action handlers
 */
export const useMissionActionHandlers = () => {
	const modalStore = useModalStore();
	const companyNeedStore = useCompanyNeedStore();
	const missionPatchStore = useMissionPatchStore();

	const handleOpenMissionDetails = (missionId: string) => {
		modalStore.actions.open(ModalName.CompanyNeedMissionDetailsModal, {
			missionId,
		});
	};

	const handleOpenMissionPropositions = (missionId: string) => {
		modalStore.actions.open(ModalName.CompanyNeedMissionDetailsModal, {
			missionId,
			initialTab: InitialTabType.propositions,
		});
	};

	const handleEditMission = async (missionId: string) => {
		const companyNeed = companyNeedStore.companyNeed;
		if (!companyNeed) return;

		const missionDetails = await missionService.fetchMissionDetails(missionId);

		modalStore.actions.open(ModalName.ManageOrderModal, {
			besoinId: companyNeed._id,
			companyId: companyNeed.companyId,
			jobId: companyNeed.jobId,
			mission: {
				id: missionId,
				...toManageOrderMissionForm(missionDetails, () =>
					mapCompanyNeedAddressToOrderAddress(companyNeed.address)
				),
			},
		});
	};

	const handleDuplicateMission = async (missionId: string) => {
		const companyNeed = companyNeedStore.companyNeed;
		if (!companyNeed) return;

		const missionDetails = await missionService.fetchMissionDetails(missionId);

		modalStore.actions.open(ModalName.ManageOrderModal, {
			besoinId: companyNeed._id,
			companyId: companyNeed.companyId,
			jobId: companyNeed.jobId,
			isDuplicate: true,
			mission: {
				...toManageOrderMissionForm(missionDetails, () =>
					mapCompanyNeedAddressToOrderAddress(companyNeed.address)
				),
			},
		});
	};

	const handleSendToEvolia = (missionId: string) => {
		console.log('Send to Evolia', missionId);
	};

	const handleExtendMission = (
		missionId: string,
		onMissionCreated?: (createdMission: MissionList) => void
	) => {
		modalStore.actions.open(ModalName.MissionExtensionModal, {
			missionId,
			onMissionCreated,
		});
	};

	const applyMissionStatus = (
		missionId: string,
		patch: MissionRowPatch & { status: MissionStatus }
	) => {
		const rollbackCount = companyNeedStore.shiftMissionsCountForStatus(
			missionId,
			patch.status
		);
		const rollbackPatch = missionPatchStore.applyPatch(missionId, patch);
		return () => {
			rollbackPatch();
			rollbackCount();
		};
	};

	const handleCancelMission = (missionId: string, onSuccess?: () => void) => {
		modalStore.actions.open(ModalName.CancelMission, {
			missionId,
			onSuccess,
			onApply: () =>
				applyMissionStatus(missionId, {
					status: MissionStatus.CANCELED,
					removedActions: CANCELED_REMOVED_ACTIONS,
				}),
		});
	};

	const handleValidateMission = (missionId: string, onSuccess?: () => void) => {
		modalStore.actions.open(ModalName.ValidateMission, {
			missionId,
			onSuccess,
			onApply: () =>
				applyMissionStatus(missionId, {
					status: MissionStatus.VALIDATED,
					removedActions: [MissionAction.VALIDATE],
				}),
		});
	};

	const handleAddWorkersMission = async (missionId: string) => {
		const missionDetails = await missionService.fetchMissionDetails(missionId);
		// The detail store only holds the missions of the company need currently displayed
		const isDisplayedCompanyNeed =
			companyNeedStore.companyNeedId === missionDetails.besoinId;
		const knownProposedQuantity =
			missionDetails.workersSelection.find(
				(selection) => selection.workerStatus === WorkerMissionStatus.PROPOSED
			)?.workers.length ?? 0;

		modalStore.actions.open(
			ModalName.CompanyNeedMissionAddWorkerToMissionModal,
			{
				missionId,
				besoinId: missionDetails.besoinId,
				missionTitle: missionDetails.name,
				excludedWorkerIds: missionDetails.workersSelection.flatMap(
					(workersSelection) =>
						workersSelection.workers.map((worker) => worker.id)
				),
				onWorkerAdded: (
					worker: MissionBulkWorker,
					options?: { bumpMissionsListQuantity: boolean }
				) => {
					if (options?.bumpMissionsListQuantity !== false) {
						missionPatchStore.bumpProposed(missionId, knownProposedQuantity);
					}
					if (!isDisplayedCompanyNeed) return;
					companyNeedStore.applyAddedWorkerToMission(missionId, worker, {
						bumpMissionsListQuantity:
							options?.bumpMissionsListQuantity !== false,
						missionSummary: {
							id: missionDetails.id,
							name: missionDetails.name,
							status: missionDetails.status,
						},
					});
				},
				onAllAdditionsDone: async () => {
					if (isDisplayedCompanyNeed) await companyNeedStore.fetchMissions();
				},
			}
		);
	};

	return {
		onDetails: handleOpenMissionDetails,
		onPropositions: handleOpenMissionPropositions,
		onEdit: handleEditMission,
		onDuplicate: handleDuplicateMission,
		onSendToEvolia: handleSendToEvolia,
		onExtend: handleExtendMission,
		onValidate: handleValidateMission,
		onCancel: handleCancelMission,
		onAddWorkers: handleAddWorkersMission,
	};
};
